/** @jsxImportSource @emotion/react */
import styled from '@emotion/styled/macro'
import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import colors from '@sportninja/common/constants/appColors'
import { resolveVideoUrl } from '@sportninja/common/utils/assetUrl'
import { Modal } from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import Text from '../../components/ts-components/Text'
import VideoPlayer from '../../components/HighlightsV2/player/VideoPlayer'
import PlayerHighlightCard from './PlayerHighlightCard'

const modalStyles = {
  header: {
    backgroundColor: colors.SECONDARY_300,
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  body: {
    borderTop: `1px solid ${colors.SOFT_STEEL}`,
    backgroundColor: colors.SECONDARY_300,
  },
  content: {
    backgroundColor: colors.SECONDARY_300,
    paddingTop: '8px',
    paddingBottom: '0',
    paddingLeft: '0',
    paddingRight: '0',
  },
}

const PlayerHighlights = ({ highlights }) => {
  const [selectedHighlight, setSelectedHighlight] = useState(null)
  const location = useLocation()

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const highlightId = params.get('highlightId')
    if (highlightId && highlights.length > 0) {
      const match = highlights.find((h) => h.id === highlightId)
      if (match && match.video_url) {
        setSelectedHighlight(match)
      }
      window.history.replaceState(null, '', location.pathname)
    }
  }, [highlights])

  const handleCardClick = (highlight) => {
    if (highlight.video_url) {
      setSelectedHighlight(highlight)
    }
  }

  return (
    <Container>
      <Card>
        <Text variant='heading5' weight='semiBold' color={colors.NEUTRAL_0}>
          Career Highlights
        </Text>
        {highlights.length === 0 ? (
          <EmptyState>
            <Text variant='paragraphSmall' color={colors.NEUTRAL_50}>
              No highlights available
            </Text>
          </EmptyState>
        ) : (
          <Grid>
            {highlights.map((highlight) => (
              <PlayerHighlightCard
                key={highlight.id}
                highlight={highlight}
                onClick={handleCardClick}
                isPlaying={selectedHighlight?.id === highlight.id}
              />
            ))}
          </Grid>
        )}
      </Card>

      <Modal
        open={!!selectedHighlight}
        onCancel={() => setSelectedHighlight(null)}
        footer={null}
        width={800}
        destroyOnClose
        centered
        title={
          <ModalTitle>
            <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
              {selectedHighlight?.title || ''}
            </Text>
          </ModalTitle>
        }
        closeIcon={<StyledCloseIcon />}
        styles={modalStyles}
      >
        <VideoPlayer url={resolveVideoUrl(selectedHighlight?.video_url)} />
      </Modal>
    </Container>
  )
}

export default PlayerHighlights

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-top: 24px;
`

const Card = styled.div`
  background-color: ${colors.SECONDARY_400};
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const EmptyState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 24px 16px;
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`

const ModalTitle = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const StyledCloseIcon = styled(CloseOutlined)`
  color: ${colors.SOLITUDE};
  font-size: 16px;
`
